import React, { createContext, useContext, useState, useEffect } from 'react';

const translations = {
  en: {
    // Navigation
    nav: {
      home: 'Home',
      about: 'About',
      services: 'Services',
      experience: 'Experience',
      partners: 'Partners',
      contact: 'Contact',
    },
    // Hero Section
    hero: {
      badge: 'Engineering Excellence Since 2022',
      title: 'Engineering',
      titleHighlight: 'The Future',
      subtitle: 'We specialize in providing design and project solutions with comprehensive engineering services, 3D modeling, rendering, and professional training.',
      cta: 'Explore Capabilities',
      ctaSecondary: 'Contact Us',
    },
    // About Section
    about: {
      badge: 'About ESOT-EU',
      title: 'Engineering',
      titleHighlight: 'Excellence',
      description1: 'ESOT-EU is an engineering company focused on turning ideas into practical, manufacturable and production-ready solutions. We work with manufacturers, machine builders and product development teams that need reliable engineering support from concept development to prototype realization and industrial implementation.',
      description2: 'Our work combines product development, manufacturing engineering, industrial automation and prototyping, allowing us to support projects across the full development cycle. The goal is not only to create a technically correct design, but to ensure that it can be produced, integrated and used efficiently in a real industrial environment.',
      description3: 'We approach every project with a strong focus on technical clarity, functionality, manufacturability and delivery discipline. Whether the task involves a new product, production equipment, process improvement or prototype validation, ESOT-EU provides engineering support built around practical execution and long-term value.',
      stats: {
        projects: 'Projects Completed',
        experience: 'Years Experience',
        clients: 'Happy Clients',
        countries: 'Countries Served',
      },
    },
    // Services Section
    services: {
      badge: 'Our Capabilities',
      title: 'Engineering',
      titleHighlight: 'Services',
      subtitle: 'Comprehensive solutions tailored to meet your engineering needs',
      items: {
        engineering: {
          title: 'Engineering',
          description: 'Full-scale engineering solutions from concept to production, ensuring optimal performance and reliability.',
        },
        design: {
          title: 'Design',
          description: 'Creative and functional design services that bring your ideas to life with precision and innovation.',
        },
        modeling: {
          title: '3D Modeling',
          description: 'Advanced 3D modeling services for visualization, prototyping, and manufacturing preparation.',
        },
        rendering: {
          title: 'Rendering',
          description: 'Photorealistic rendering services to visualize your products before production.',
        },
        animation: {
          title: 'Animation',
          description: 'Professional animation services for product demonstrations and technical presentations.',
        },
        renderingAnimation: {
          title: 'Rendering & Animation',
          description: 'Technical visualization for design reviews, customer presentations and marketing materials.',
        },
        training: {
          title: 'Training',
          description: 'Professional training in CATIA V5 and tolerance calculation to enhance your team\'s skills.',
        },
        prototyping: {
          title: 'Prototyping',
          description: 'Rapid prototyping services using traditional and modern manufacturing methods.',
        },
        automation: {
          title: 'Industrial Automation',
          description: 'Custom automation solutions for manufacturing, robot integration and process control.',
        },
      },
    },
    // Experience Section
    experience: {
      badge: 'Our Experience',
      title: 'Proven',
      titleHighlight: 'Expertise',
      subtitle: 'Years of experience across various industries with successful partnerships worldwide',
      features: {
        quality: {
          title: 'Quality Driven',
          description: 'Our commitment to quality drives us to deliver exceptional services and products.',
        },
        timely: {
          title: 'Timely Delivery',
          description: 'We recognize that precise timing is crucial for the success of modern businesses.',
        },
        innovative: {
          title: 'Innovative Solutions',
          description: 'ESOT-EU continues to innovate and develop engineering solutions for modern industry.',
        },
        global: {
          title: 'Global Reach',
          description: 'We collaborate with clients worldwide, assisting them in achieving project success.',
        },
      },
    },
    // Contact Section
    contact: {
      badge: 'Get In Touch',
      title: 'Start Your',
      titleHighlight: 'Project',
      subtitle: 'Ready to bring your ideas to life? Contact us for a free evaluation.',
      form: {
        name: 'Your Name',
        email: 'Email Address',
        company: 'Company (Optional)',
        message: 'Your Message',
        submit: 'Send Message',
        sending: 'Sending...',
        success: 'Message sent successfully! We\'ll get back to you soon.',
        error: 'Failed to send message. Please try again.',
      },
      info: {
        title: 'Contact Information',
        description: 'For more information about the services we offer, please don\'t hesitate to contact us.',
        email: 'Email',
        location: 'Location',
        availability: 'Availability',
        availabilityText: 'Mon - Fri, 9:00 - 18:00 EET',
      },
    },
    // Footer
    footer: {
      description: 'Engineering company providing design and project solutions with comprehensive services.',
      links: 'Quick Links',
      services: 'Services',
      contact: 'Contact',
      rights: 'All rights reserved.',
    },
  },
  bg: {
    // Navigation
    nav: {
      home: 'Начало',
      about: 'За нас',
      services: 'Услуги',
      experience: 'Опит',
      partners: 'Партньори',
      contact: 'Контакт',
    },
    // Hero Section
    hero: {
      badge: 'Инженерно съвършенство от 2022',
      title: 'Инженеринг на',
      titleHighlight: 'Бъдещето',
      subtitle: 'Специализираме се в предоставяне на дизайнерски и проектни решения с комплексни инженерни услуги, 3D моделиране, рендериране и професионално обучение.',
      cta: 'Разгледайте възможностите',
      ctaSecondary: 'Свържете се с нас',
    },
    // About Section
    about: {
      badge: 'За ESOT-EU',
      title: 'Инженерно',
      titleHighlight: 'Съвършенство',
      description1: 'ESOT-EU е инженерна компания, фокусирана върху превръщането на идеи в практически, производими и готови за производство решения. Работим с производители, машиностроители и екипи за разработка на продукти, които се нуждаят от надеждна инженерна поддръжка от разработката на концепцията до реализацията на прототипа и индустриалното внедряване.',
      description2: 'Нашата работа съчетава разработка на продукти, производствено инженерство, индустриална автоматизация и прототипиране, което ни позволява да поддържаме проекти през целия цикъл на разработка. Целта е не само да създадем технически правилен дизайн, но и да гарантираме, че той може да бъде произведен, интегриран и използван ефективно в реална индустриална среда.',
      description3: 'Подхождаме към всеки проект със силен фокус върху техническата яснота, функционалността, производимостта и дисциплината на доставка. Независимо дали задачата включва нов продукт, производствено оборудване, подобряване на процеса или валидиране на прототип, ESOT-EU предоставя инженерна поддръжка, изградена около практическо изпълнение и дългосрочна стойност.',
      stats: {
        projects: 'Завършени проекти',
        experience: 'Години опит',
        clients: 'Доволни клиенти',
        countries: 'Обслужени държави',
      },
    },
    // Services Section
    services: {
      badge: 'Нашите възможности',
      title: 'Инженерни',
      titleHighlight: 'Услуги',
      subtitle: 'Комплексни решения, съобразени с вашите инженерни нужди',
      items: {
        engineering: {
          title: 'Инженеринг',
          description: 'Пълномащабни инженерни решения от концепция до производство, осигуряващи оптимална производителност и надеждност.',
        },
        design: {
          title: 'Дизайн',
          description: 'Креативни и функционални дизайнерски услуги, които оживяват вашите идеи с прецизност и иновация.',
        },
        modeling: {
          title: '3D Моделиране',
          description: 'Разширени услуги за 3D моделиране за визуализация, прототипиране и подготовка за производство.',
        },
        rendering: {
          title: 'Рендериране',
          description: 'Фотореалистични услуги за рендериране, за да визуализирате вашите продукти преди производство.',
        },
        animation: {
          title: 'Анимация',
          description: 'Професионални анимационни услуги за продуктови демонстрации и технически презентации.',
        },
        renderingAnimation: {
          title: 'Рендериране и Анимация',
          description: 'Техническа визуализация за прегледи на дизайн, презентации за клиенти и маркетингови материали.',
        },
        training: {
          title: 'Обучение',
          description: 'Професионално обучение по CATIA V5 и изчисляване на толеранси за подобряване на уменията на вашия екип.',
        },
        prototyping: {
          title: 'Прототипиране',
          description: 'Услуги за бързо прототипиране, използвайки традиционни и модерни производствени методи.',
        },
        automation: {
          title: 'Индустриална автоматизация',
          description: 'Персонализирани решения за автоматизация за производство, интеграция на роботи и контрол на процеси.',
        },
      },
    },
    // Experience Section
    experience: {
      badge: 'Нашият опит',
      title: 'Доказана',
      titleHighlight: 'Експертиза',
      subtitle: 'Години опит в различни индустрии с успешни партньорства по целия свят',
      features: {
        quality: {
          title: 'Ориентирани към качество',
          description: 'Нашият ангажимент към качеството ни движи да доставяме изключителни услуги и продукти.',
        },
        timely: {
          title: 'Навременна доставка',
          description: 'Признаваме, че прецизното време е от решаващо значение за успеха на съвременния бизнес.',
        },
        innovative: {
          title: 'Иновативни решения',
          description: 'ESOT-EU продължава да иновира и развива инженерни решения за модерната индустрия.',
        },
        global: {
          title: 'Глобален обхват',
          description: 'Сътрудничим с клиенти по целия свят, като им помагаме да постигнат успех в проектите си.',
        },
      },
    },
    // Contact Section
    contact: {
      badge: 'Свържете се',
      title: 'Започнете вашия',
      titleHighlight: 'Проект',
      subtitle: 'Готови ли сте да оживите вашите идеи? Свържете се с нас за безплатна оценка.',
      form: {
        name: 'Вашето име',
        email: 'Имейл адрес',
        company: 'Компания (по избор)',
        message: 'Вашето съобщение',
        submit: 'Изпрати съобщение',
        sending: 'Изпращане...',
        success: 'Съобщението е изпратено успешно! Ще се свържем с вас скоро.',
        error: 'Неуспешно изпращане на съобщение. Моля, опитайте отново.',
      },
      info: {
        title: 'Информация за контакт',
        description: 'За повече информация относно услугите, които предлагаме, не се колебайте да се свържете с нас.',
        email: 'Имейл',
        location: 'Местоположение',
        availability: 'Работно време',
        availabilityText: 'Пон - Пет, 9:00 - 18:00 EET',
      },
    },
    // Footer
    footer: {
      description: 'Инженерна компания, предоставяща дизайнерски и проектни решения с комплексни услуги.',
      links: 'Бързи връзки',
      services: 'Услуги',
      contact: 'Контакт',
      rights: 'Всички права запазени.',
    },
  },
};

const LanguageContext = createContext();

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('esot-language') || 'en';
    }
    return 'en';
  });

  useEffect(() => {
    localStorage.setItem('esot-language', language);
  }, [language]);

  const t = (key) => {
    const keys = key.split('.');
    let value = translations[language];
    for (const k of keys) {
      value = value?.[k];
    }
    return value || key;
  };

  const toggleLanguage = () => {
    setLanguage(prev => prev === 'en' ? 'bg' : 'en');
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, toggleLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
